import { Box, Typography, Stack } from '@mui/material'
import { C1IconButton } from 'c1ui'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { C1Icon } from 'components/core/icons/C1Icon'
import { CheckCircle, CloudUpload, Paperclip, X } from 'lucide-react'
import { FC, useCallback, useState, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

/** Sentinel value used in form state to indicate a file was previously uploaded */
export const PLACEHOLDER_FILE_UPLOADED = '__c1_file_previously_uploaded__'

export interface C1FileInputProps {
    label: string
    name: string
    value?: File
    onChange: (value: File | undefined) => void
    onBlur?: () => void
    error?: boolean
    helperText?: string
    disabled?: boolean
    acceptedFileTypes?: string[]
    /** Display max file size hint inside dropzone (e.g., "1 GB") */
    maxFileSizeHint?: string
    /** Use 'expanded' for a taller, centered dropzone layout */
    variant?: 'default' | 'expanded'
    /** When true, shows a "previously uploaded" state instead of the empty dropzone */
    hasExistingFile?: boolean
}

export const C1FileInput: FC<C1FileInputProps> = ({
    label,
    name,
    value,
    onChange,
    onBlur,
    error = false,
    helperText,
    disabled = false,
    acceptedFileTypes = [],
    maxFileSizeHint,
    variant = 'default',
    hasExistingFile = false,
}) => {
    const isExpanded = variant === 'expanded'
    const [dragActive, setDragActive] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const dropzoneRef = useRef<HTMLDivElement>(null)

    const intl = useIntl()

    const hasNewFile = value instanceof File
    const showExisting = hasExistingFile && !hasNewFile

    const handleFile = useCallback((file: File): void => {
        onChange(file)
        onBlur?.()
    }, [onChange, onBlur])

    const handleClear = useCallback((): void => {
        onChange(undefined)
        onBlur?.()
        // Clear the file input element
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }, [onChange, onBlur])

    const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
        const files = e.target.files
        if (files && files.length > 0) handleFile(files[0])
    }, [handleFile])

    const handleDragOver = useCallback((e: React.DragEvent): void => {
        e.preventDefault()
        setDragActive(true)
    }, [])

    const handleDragLeave = useCallback((e: React.DragEvent): void => {
        e.preventDefault()
        setDragActive(false)
    }, [])

    const handleDrop = useCallback((e: React.DragEvent): void => {
        e.preventDefault()
        e.stopPropagation()
        setDragActive(false)

        if (disabled) return

        const files: File[] = []

        if (e.dataTransfer.items) {
            const items = e.dataTransfer.items

            for (let i = 0; i < items.length; i++) {
                if (items[i].kind === 'file') {
                    const file = items[i].getAsFile()
                    if (file) files.push(file)
                }
            }
        } else {
            for (let i = 0; i < e.dataTransfer.files.length; i++) {
                const file = e.dataTransfer.files[i]
                files.push(file)
            }
        }

        if (files.length > 0) handleFile(files[0])
    }, [disabled, handleFile])

    const getAcceptString = (): string => (acceptedFileTypes.length === 0 ? '*/*' : acceptedFileTypes.join(','))

    const getBorderColor = (): string => {
        if (error) return 'error.main'
        if (hasNewFile || showExisting || dragActive) return 'primary.main'
        return 'border.emphasis'
    }

    return (
        <Box>
            <Box mb={1}>
                <Typography>{label}</Typography>
                {helperText && <Typography color={error ? 'error' : 'text.secondary'}>{helperText}</Typography>}
            </Box>

            <Box
                ref={dropzoneRef}
                tabIndex={disabled ? undefined : 0}
                role="button"
                aria-label={intl.formatMessage({ id: 'file_field_dropzone_label', defaultMessage: 'Upload file' })}
                sx={{
                    p: isExpanded ? 4 : 2,
                    minHeight: isExpanded ? 140 : undefined,
                    borderWidth: 1,
                    borderStyle: (hasNewFile || showExisting) ? 'solid' : 'dashed',
                    borderColor: getBorderColor(),
                    borderRadius: C1BorderRadius.large,
                    backgroundColor: dragActive ? 'primary.extraLight' : 'background.default',
                    cursor: disabled ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isExpanded && !hasNewFile && !showExisting ? 'center' : 'flex-start',
                    outline: 'none',

                    '&:focus-visible': {
                        borderColor: error ? 'error.main' : 'primary.main',
                        boxShadow: (theme): string => `0 0 0 2px ${error ? theme.palette.error.main : theme.palette.primary.main}`,
                    },
                    '&:hover': {
                        borderColor: disabled ? 'border.emphasis' : error ? 'error.main' : 'primary.main',
                        backgroundColor: disabled ? 'background.default' : 'primary.extraLight',
                    },
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={(): void => {
                    if (!disabled) fileInputRef.current?.click()
                }}
                onKeyDown={(e): void => {
                    if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault()
                        fileInputRef.current?.click()
                    }
                }}
            >
                <Stack
                    direction={isExpanded && !hasNewFile && !showExisting ? 'column' : 'row'}
                    alignItems="center"
                    spacing={2}
                    sx={{ width: isExpanded && !hasNewFile && !showExisting ? 'auto' : '100%' }}
                >
                    {hasNewFile ? <>
                        <C1Icon icon={Paperclip} sx={{ color: 'primary.main' }} />
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {value.name}
                            </Typography>
                            <Typography color="text.secondary" variant="caption">
                                {`${(value.size / 1024).toFixed(1)} KB`}
                            </Typography>
                        </Box>
                        <C1IconButton
                            variant="text"
                            aria-label={intl.formatMessage({ id: 'file_field_clear', defaultMessage: 'Clear' })}
                            onClick={(e): void => {
                                e.stopPropagation()
                                handleClear()
                            }}
                            disabled={disabled}
                        >
                            <C1Icon icon={X} />
                        </C1IconButton>
                    </> : showExisting ? <>
                        <C1Icon icon={CheckCircle} sx={{ color: 'success.main' }} />
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                <FormattedMessage
                                    id="file_field_previously_uploaded"
                                    defaultMessage="File previously uploaded"
                                />
                            </Typography>
                            <Typography color="text.secondary" variant="caption">
                                <FormattedMessage
                                    id="file_field_replace_hint"
                                    defaultMessage="Click to replace, or clear"
                                />
                            </Typography>
                        </Box>
                        {!disabled && (
                            <C1IconButton
                                variant="text"
                                aria-label={intl.formatMessage({ id: 'file_field_clear', defaultMessage: 'Clear' })}
                                onClick={(e): void => {
                                    e.stopPropagation()
                                    handleClear()
                                }}
                            >
                                <C1Icon icon={X} />
                            </C1IconButton>
                        )}
                    </> : <>
                        <C1Icon icon={CloudUpload} sx={{ color: error ? 'error.main' : 'text.secondary', fontSize: isExpanded ? 32 : 24 }} />
                        <Box sx={{ flexGrow: isExpanded ? 0 : 1, textAlign: isExpanded ? 'center' : 'left' }}>
                            <Typography color={error ? 'error' : 'text.secondary'} variant="body2">
                                <FormattedMessage
                                    id="file_field_drag_drop_text"
                                    defaultMessage="Drag and drop a file here, or click to select"
                                />
                            </Typography>
                            {acceptedFileTypes.length > 0 && (
                                <Typography color="text.secondary" variant="caption">
                                    <FormattedMessage
                                        id="file_field_accepted_types"
                                        defaultMessage="Accepted types: {types}"
                                        values={{ types: acceptedFileTypes.join(', ') }}
                                    />
                                </Typography>
                            )}
                            {maxFileSizeHint && (
                                <Typography color="text.secondary" variant="caption">
                                    <FormattedMessage
                                        id="file_field_max_size"
                                        defaultMessage="Up to {size}"
                                        values={{ size: maxFileSizeHint }}
                                    />
                                </Typography>
                            )}
                        </Box>
                    </>}
                </Stack>
            </Box>

            <input
                ref={fileInputRef}
                type="file"
                name={name}
                accept={getAcceptString()}
                onChange={handleFileInput}
                onBlur={onBlur}
                style={{ display: 'none' }}
                disabled={disabled}
            />
        </Box>
    )
}
